/**
 * Fixtures for the surfaces that exist before a project record does.
 *
 * The landing hero, the name field and the repository picker all render data
 * that has no home in the seeded account, because at that point in the flow
 * there is no account and no project yet. It lives here rather than inside a
 * component for the same reason everything else lives in a fixture: a screen
 * that invents a number is a screen nobody can change the demo narrative from.
 *
 * PUBLIC_DOMAIN sits in this file because the first screen that needs an
 * address stem is the screen where no environment record exists to read it
 * from. seed.ts imports it, so the product and the onboarding preview always
 * print the same ending, and moving the whole prototype to a different stem is
 * one edit here.
 */

import type { RepoCandidate } from '../types';

/**
 * The three onboarding steps, in order, as English keys.
 *
 * They live here for the same reason the build script's step labels do: a
 * label that is data can be rendered by three screens without any of them
 * importing another screen, and t() is called at render, never here.
 */
export const ONBOARDING_STEPS = ['Name your app', 'Connect your code', 'Build it'];

/** The address stem every web address in the product ends with. Screen spec 5. */
export const PUBLIC_DOMAIN = 'apps.sv-academy.org';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const ISO = (msAgo: number) => new Date(Date.now() - msAgo).toISOString();

/* ------------------------------------------------------------ the name */

/**
 * Addresses SV Cloud keeps for itself.
 *
 * The denylist is never explained to anyone. Typing one produces a sentence
 * that names the alternative, which is how a person learns the rule exists
 * without being handed a rule.
 */
export const RESERVED_NAMES: string[] = [
  'account',
  'admin',
  'api',
  'app',
  'apps',
  'assets',
  'billing',
  'blog',
  'cdn',
  'cloud',
  'console',
  'dashboard',
  'docs',
  'help',
  'mail',
  'new',
  'signin',
  'signup',
  'static',
  'status',
  'support',
  'sv',
  'svcloud',
  'www',
];

/**
 * Addresses somebody else already has.
 *
 * The seeded projects are in here so the person who types the name of a
 * project that is on screen elsewhere in the prototype gets the honest answer.
 */
export const TAKEN_NAMES: string[] = [
  'herb-and-ash',
  'ledger',
  'notes',
  'portfolio',
  'recipes',
  'shop',
  'soi-cats',
  'studio-ledger',
  'wedding-rsvp',
];

/* ------------------------------------------------------- the repositories */

/**
 * Six repositories, mixed empty and not, one of them pushed to minutes ago.
 *
 * The owner is the account's own handle, so the picker never shows a stranger's
 * name back to the person who just signed in with GitHub.
 */
export function repoCandidates(handle: string): RepoCandidate[] {
  return [
    {
      id: 'repo_studio_ledger',
      owner: handle,
      name: 'studio-ledger',
      defaultBranch: 'main',
      isEmpty: false,
      pushedAt: ISO(12 * MINUTE),
    },
    {
      id: 'repo_weekend_menu',
      owner: handle,
      name: 'weekend-menu',
      defaultBranch: 'main',
      isEmpty: true,
      pushedAt: ISO(20 * MINUTE),
    },
    {
      id: 'repo_herb_and_ash',
      owner: handle,
      name: 'herb-and-ash',
      defaultBranch: 'main',
      isEmpty: false,
      pushedAt: ISO(2 * HOUR),
    },
    {
      id: 'repo_pantry_notes',
      owner: handle,
      name: 'pantry-notes',
      defaultBranch: 'main',
      isEmpty: true,
      pushedAt: ISO(3 * DAY),
    },
    {
      id: 'repo_soi_cats',
      owner: handle,
      name: 'soi-cats',
      defaultBranch: 'main',
      isEmpty: false,
      pushedAt: ISO(6 * DAY),
    },
    {
      id: 'repo_old_portfolio',
      owner: handle,
      name: 'old-portfolio',
      defaultBranch: 'main',
      // Six days, not six months, and the reason is a copy rule rather than a
      // narrative one: relativeTime() in lib/format has no absolute date
      // branch, so anything past a week prints "96 days ago" and breaks the
      // rule that time goes relative under 7 days and absolute beyond.
      isEmpty: false,
      pushedAt: ISO(6 * DAY + 4 * HOUR),
    },
  ];
}

/** Above this many repositories the picker grows a search field. Screen spec S4. */
export const REPO_SEARCH_THRESHOLD = 8;

/* ---------------------------------------------------------- the commit */

/** The one template that exists, labelled by its real name. Advanced only. */
export const STARTER_TEMPLATE = 'sv-starter-web';

export interface StarterFile {
  path: string;
  bytes: number;
}

/** Exactly what lands in the repository, with sizes. Advanced only. */
export const STARTER_FILES: StarterFile[] = [
  { path: 'sv.config.json', bytes: 412 },
  { path: 'package.json', bytes: 1_024 },
  { path: 'README.md', bytes: 1_842 },
  { path: '.gitignore', bytes: 268 },
  { path: 'index.html', bytes: 1_130 },
  { path: 'src/main.ts', bytes: 2_318 },
  { path: 'src/app.ts', bytes: 4_602 },
  { path: 'src/styles.css', bytes: 3_104 },
  { path: 'src/lib/data.ts', bytes: 1_960 },
  { path: 'migrations/0001_init.sql', bytes: 1_488 },
  { path: 'public/favicon.png', bytes: 6_144 },
];

/* ------------------------------------------------------------- the name */

/**
 * The slug derivation, made visible under the field.
 *
 * It normalizes silently: spaces become hyphens, punctuation is dropped, case
 * is folded. There is no error state for typing normally, and there never will
 * be one. Scolding someone for typing their project's name the way they say it
 * is the fastest way to make them feel this product is not for them.
 */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40)
    .replace(/-$/, '');
}

export type NameVerdict = 'idle' | 'checking' | 'available' | 'taken' | 'reserved';

/** Pure, so the availability rules are testable without a screen. */
export function verdictFor(slug: string): Exclude<NameVerdict, 'idle' | 'checking'> {
  if (RESERVED_NAMES.includes(slug)) return 'reserved';
  if (TAKEN_NAMES.includes(slug)) return 'taken';
  return 'available';
}

/** The alternative offered as a click to apply chip when a name is taken. */
export function suggestionFor(slug: string): string {
  const match = /^(.*?)-(\d+)$/.exec(slug);
  const stem = match ? match[1] : slug;
  let n = match ? Number(match[2]) + 1 : 2;
  while (verdictFor(`${stem}-${n}`) !== 'available') n += 1;
  return `${stem}-${n}`;
}
